import { useRef, useState } from 'react';
import itemService from '@/services/billing/itemService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon, DefaultTooltip } from '@/components';
import { Can } from '@casl/react';
import { ItemsModal } from '@/pages/billing';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import { FiltersToFormData, getAccessToken } from '@/utils';
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { SelectInput, DateInput, TextInput, STATUS_OPTIONS, STATUS_NUMBER_OPTIONS, FilterContainer, ColumnToggleDropdown } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import taxService from '@/services/billing/taxService';
import { goTo403 } from "@/utils/Navigation";
import { useUserOptions, useTaxOptions } from '@/hooks'
import { Container } from '@/components/container';

DataTable.use(DT);
const Items = () => {
  const { logout } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [contactTypeData, setContactTypeData] = useState([])
  const [taxesData, setTaxesData] = useState([])
  const [itemId, setItemId] = useState(null)
  const { ability } = useAbility();
  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef({});
  const formikRef = useRef();
  const canDeleteItem = ability.can('delete', 'items');
  const canEditItem = ability.can('handle', 'items');
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const [filterData, setFilterData] = useState({
    created_at: null,
    taxes: null,
    from_date: null,
    to_date: null,
    date_range: null,
  });
  const [showFilter, setShowFilter] = useState(false)
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });
  const loadTaxOptions = useTaxOptions({ defaultPageSize: 20 });


  const columns = [
    // {
    //   title: 'S#',
    //   name: 'serial',
    //   data: null,
    //orderable: true,se,
    //   render: (data, type, row, meta) => meta.row + 1,
    // },
    {
      data: 'name',
      title: 'Name',
      name: 'name'
    },
    {
      data: 'code',
      title: 'Code',
      render: function (data, type, row) {
        if (row.code === null) return '-';
        return `<div class="text-xs">${row.code}</div>`;
      },
      name: 'code'
    },
    {
      data: 'price',
      title: 'Price',
      render: function (data, type, row) {
        if (row.price === null) return '$0.00';
        return `<div>$${row.price}</div>`;
      },
      name: 'price'
    },
    {
      data: 'created_at',
      title: 'Created At',
      name: 'created_at',
      orderable: true,
    },
    {
      title: 'Actions',
      data: null,
      orderable: true,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="action-cell-${row.id}"></div>`;
      },
      name: 'actions'
    },

    // --- Extended Fields ---
    // { data: 'id', title: 'ID', name: 'id' },
    {
      data: 'description',
      title: 'Description',
      name: 'description',
      render: function (data, type, row) {
        if (!row.description) return '-';
        return `<div class="text-sm">${row.description}</div>`;
      }
    },
    {
      data: 'taxes',
      title: 'Taxes',
      name: 'taxes',
      orderable: true,
      searchable: false,
      render: function (data, type, row) {
        if (!Array.isArray(row.taxes) || row.taxes.length === 0) {
          return `<span class="text-xs text-gray-500">No Taxes</span>`;
        }
        return `<div id="taxes-cell-${row.id}"></div>`;
      }
    },
    // {
    //   data: 'deleted_at',
    //   title: 'Deleted At',
    //   name: 'deleted_at',
    //   render: function (data, type, row) {
    //     return row.deleted_at ? row.deleted_at : '-';
    //   }
    // },
    // {
    //   data: 'updated_at',
    //   title: 'Updated At',
    //   name: 'updated_at'
    // },
    // {
    //   data: 'DT_RowIndex',
    //   title: 'Row Index',
    //   name: 'DT_RowIndex'
    // }
  ];


  const defaultVisibleColumns = [
    // 'serial',
    'name',
    'code',
    'price',
    'created_at',
    'actions'
  ];


  const handleSubmit = async (formData) => {
    const isEdit = mode === 'edit';
    const action = isEdit ? itemService.updateItem : itemService.createItem;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { data, status } = await action(...args);
      if (status) {
        handleModalClose();
        refresh();
        toast.success(`Item ${isEdit ? 'Updated' : 'Created'} Successfully`, {
          description: `${data.name} Item ${isEdit ? 'updated' : 'created'} successfully.`,

        })
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const validationErrors = error.errors
        const formattedErrors = Object.fromEntries(
          Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
        );

        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',

        });
      }
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    closeModal()
    try {
      const { status, data } = await itemService.deleteItem(actionId);
      if (status) {
        refresh()
        toast.success('Item Deleted.', {
          description: `${data.name} Item deleted successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message.includes(`Can't delete:`)) {
        toast.error('Failed to delete.', {
          description: 'The selected item is in use.',
        });
        return
      }
      toast.error('Failed to delete Item.', {
        description: 'An error occurred while deleting Item. Please try again later.',
      });
    }
  }

  const handleEdit = async (id) => {
    setItemId(id)
    setOpen(true)
    setIsLoading(true)
    setMode('edit')
    try {
      const { data, status } = await itemService.getSingleItem(id);
      if (status) {
        setSelectedData(data)
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to fetch user.', {
        description: 'An error occurred while fetching user. Please try again later.',
        action: {
          label: 'Ok',
        },
      });
    } finally {
      setIsLoading(false)
    }
  }

  const handleModalClose = () => {
    setItemId(null)
    setSelectedData(null)
    setMode('create')
    setOpen(false)
  }

  const renderCustomActions = (row) => {
    const { id } = row;

    return (
      <DropdownMenu>
        {(canDeleteItem || canEditItem) && (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-600">
              <KeenIcon icon="dots-vertical" className="text-[18px]" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-40">
          <Can I="handle" a="items" ability={ability}>
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
            </DropdownMenuItem>
          </Can>
          <Can I="delete" a="items" ability={ability}>
            <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderTaxes = (row) => {
    const { taxes } = row;
    return (
      <>
        {taxes.map((tax, index) => (
          <DefaultTooltip key={index} title={`Rate: $${tax.rate}`} placement="top" className="max-w-full">
            <span className='mb-1 mr-1 capitalize badge badge-sm badge-pill badge-outline badge-primary'>{tax.name}</span>
          </DefaultTooltip>
        ))}
      </>
    )
  }

  const ajaxConfig = createDataTableConfig('/setup/items/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[3, 'desc']],

    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
    },

    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();

      rows.each((row) => {
        const container = document.getElementById(`action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderCustomActions(row));
        }

        const Taxescontainer = document.getElementById(`taxes-cell-${row.id}`);
        if (Taxescontainer) {
          if (!Taxescontainer._reactRoot) {
            Taxescontainer._reactRoot = createRoot(Taxescontainer);
          }
          Taxescontainer._reactRoot.render(renderTaxes(row));
        }

      });
    },
  });

  const refresh = () => {
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  const resetFilters = () => {
    const cleared = {
      created_at: null,
      taxes: null,
      from_date: null,
      to_date: null,
      date_range: null,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      //console.log('updated',updated)
      return updated;
    });
  };


  return (
    <>
      <Container>
        {/* Filters */}
        <FilterContainer
          show={showFilter}
          onApply={() => refresh()}
          onReset={resetFilters}
        >
          {/* <SelectInput
          label="tax"
          type="tax"
          isMulti={true}
          value={filterData.tax}
          onChange={(val) => handleFilterChange('taxes', val)}
          loadOptions={loadTaxOptions}
          pageSize={20}
          customClass="sm:w-[32%] w-full"
        /> */}

          <DateInput
            customClass="sm:w-[32%] w-full"
            label="Created At - (From, To)"
            mode="range"
            value={filterData.date_range}
            onChange={(range) => {
              handleFilterChange('from_date', range?.from ?? null);
              handleFilterChange('to_date', range?.to ?? null);
              handleFilterChange('date_range', range);
            }}
          />
        </FilterContainer>

        <div className="min-w-full mb-4 card">
          <div className="card-header">
            <div className='flex items-center gap-1'>
              <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
              {/* <h3 className="card-title">Items</h3> */}
            </div>

            <div className='flex flex-wrap items-center gap-2'>
              <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                <KeenIcon icon="filter" className="w-fit btn-icon" />
                {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
              </button>
              <Can I="handle" a="items" ability={ability}>
                <button className='text-xs btn btn-sm btn-light' onClick={() => { setOpen(true) }}>
                  <KeenIcon icon="plus" className="w-fit btn-icon" />
                  Add New
                </button>
              </Can>
            </div>
          </div>


          <DataTable
            id="item-table"
            ajax={ajaxConfig}
            columns={columns}
            options={options}
            ref={tableRef}
          />

          <ItemsModal
            open={open}
            onCloseModal={handleModalClose}
            handleMode={mode}
            data={selectedData}
            onSubmit={handleSubmit}
            createFieldLoading={isLoading}
            updateFieldLoading={loading}
            taxesData={taxesData}
            ref={formikRef}
          />

          <ConfirmationModal
            show={isOpen}
            onClose={closeModal}
            onConfirm={handleDelete}
            message="Are you sure you want to delete this Item? This action is permanent and cannot be undone."
            title="Are You Sure?"
          />
        </div>
      </Container>
    </>
  )
}

export { Items }
